import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Upload, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

const inputCls =
  "w-full bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-4 py-3 rounded-sm text-white transition-colors";

const AVAIL_OPTS = [
  { key: "green", label: "Looking for a club", dot: "bg-green-500" },
  { key: "yellow", label: "Open to offers", dot: "bg-yellow-400" },
  { key: "red", label: "Not looking", dot: "bg-red-500" },
];

export default function WelcomePage() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    avatar_url: "",
    bio: "",
    height_cm: "",
    preferred_foot: "",
    level_of_play: "",
    availability: "",
  });

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, avatar_url: `${api.defaults.baseURL}/media/${data.path}` }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const savePartial = async (fields) => {
    const payload = {};
    for (const k of fields) {
      if (form[k] !== "" && form[k] !== undefined) {
        payload[k] = k === "height_cm" ? Number(form[k]) : form[k];
      }
    }
    if (Object.keys(payload).length === 0) return;
    await api.patch("/users/me", payload);
  };

  const next = async (fields) => {
    setSaving(true);
    try {
      await savePartial(fields);
      setStep((s) => s + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const finishSetup = async () => {
    setSaving(true);
    try {
      await savePartial(["availability"]);
      await refresh();
      toast.success("Profile set up — welcome to EGNCY");
      nav(`/profile/${user.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;
  const firstName = user.name?.split(" ")[0] || "there";

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-10" data-testid="welcome-page">
      <div className="w-full max-w-lg">
        <h1 className="font-display text-5xl uppercase tracking-tight text-white mb-1">EGNCY</h1>
        <p className="text-sm text-[#a3a3a3] mb-6" data-testid="welcome-greeting">
          Welcome, {firstName}. Let&apos;s make your profile scout-ready — {step} of 3.
        </p>
        <div className="flex gap-1 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1 flex-1 rounded-sm ${s <= step ? "bg-white" : "bg-[#262626]"}`} />
          ))}
        </div>

        <div className="bg-[#121212] border border-[#262626] rounded-sm p-6 space-y-4">
          {step === 1 && (
            <>
              <h2 className="font-display text-2xl uppercase tracking-tight text-white">Show your face</h2>
              <p className="text-sm text-[#a3a3a3]">
                Profiles with a photo get noticed first by scouts.
              </p>
              <label className="block">
                <div className="flex items-center gap-3 border border-dashed border-[#404040] hover:border-white p-4 rounded-sm cursor-pointer transition-colors">
                  {form.avatar_url ? (
                    <img src={form.avatar_url} alt="" className="w-14 h-14 rounded-full object-cover border border-[#262626]" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-[#0a0a0a] border border-[#262626] flex items-center justify-center">
                      <Upload className="w-5 h-5 text-[#a3a3a3]" />
                    </div>
                  )}
                  <span className="text-sm text-[#a3a3a3]">
                    {form.avatar_url ? "Looking sharp — tap to replace" : "Upload a profile photo"}
                  </span>
                  <input data-testid="welcome-avatar-upload" type="file" accept="image/*" className="hidden" onChange={uploadAvatar} />
                </div>
              </label>
              <textarea
                data-testid="welcome-bio"
                value={form.bio}
                onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                rows={3}
                placeholder="One or two lines about your game — e.g. 'Quick left winger, loves taking on defenders.'"
                className={`${inputCls} resize-none`}
              />
              <StepFooter onSkip={() => setStep(2)} onNext={() => next(["avatar_url", "bio"])} saving={saving} />
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="font-display text-2xl uppercase tracking-tight text-white">Your numbers</h2>
              <p className="text-sm text-[#a3a3a3]">
                Scouts filter by these — filling them in puts you in more searches.
              </p>
              <div className="grid grid-cols-3 gap-3">
                <input
                  data-testid="welcome-height"
                  type="number" min="100" max="230"
                  value={form.height_cm}
                  onChange={(e) => setForm((f) => ({ ...f, height_cm: e.target.value }))}
                  placeholder="Height (cm)"
                  className="bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-3 py-3 rounded-sm text-white transition-colors"
                />
                <select
                  data-testid="welcome-foot"
                  value={form.preferred_foot}
                  onChange={(e) => setForm((f) => ({ ...f, preferred_foot: e.target.value }))}
                  className="bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-3 py-3 rounded-sm text-white transition-colors"
                >
                  <option value="">Foot</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                  <option value="both">Both</option>
                </select>
                <select
                  data-testid="welcome-level"
                  value={form.level_of_play}
                  onChange={(e) => setForm((f) => ({ ...f, level_of_play: e.target.value }))}
                  className="bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-3 py-3 rounded-sm text-white transition-colors"
                >
                  <option value="">Level</option>
                  <option value="amateur">Amateur</option>
                  <option value="semi-pro">Semi-pro</option>
                  <option value="pro">Pro</option>
                </select>
              </div>
              <StepFooter onSkip={() => setStep(3)} onNext={() => next(["height_cm", "preferred_foot", "level_of_play"])} saving={saving} />
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="font-display text-2xl uppercase tracking-tight text-white">Are you available?</h2>
              <p className="text-sm text-[#a3a3a3]">
                Your availability light shows on your profile and in scout searches.
              </p>
              <div className="grid grid-cols-1 gap-2">
                {AVAIL_OPTS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    data-testid={`welcome-availability-${opt.key}`}
                    onClick={() => setForm((f) => ({ ...f, availability: f.availability === opt.key ? "" : opt.key }))}
                    className={`flex items-center gap-3 px-4 py-3 border rounded-sm text-sm text-left transition-colors ${
                      form.availability === opt.key
                        ? "border-white bg-[#1a1a1a] text-white"
                        : "border-[#262626] text-[#a3a3a3] hover:border-white hover:text-white"
                    }`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${opt.dot}`} />
                    {opt.label}
                    {form.availability === opt.key && <Check className="w-4 h-4 ml-auto" />}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  data-testid="welcome-skip"
                  onClick={async () => { await refresh(); nav("/"); }}
                  className="text-sm text-[#737373] hover:text-white transition-colors"
                >
                  Skip for now
                </button>
                <button
                  type="button"
                  data-testid="welcome-finish"
                  onClick={finishSetup}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-white text-black font-semibold rounded-sm hover:bg-gray-200 disabled:opacity-60 transition-colors"
                >
                  {saving ? "Saving…" : "See my profile"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StepFooter({ onSkip, onNext, saving }) {
  return (
    <div className="flex items-center justify-between pt-2">
      <button
        type="button"
        data-testid="welcome-step-skip"
        onClick={onSkip}
        className="text-sm text-[#737373] hover:text-white transition-colors"
      >
        Skip
      </button>
      <button
        type="button"
        data-testid="welcome-step-next"
        onClick={onNext}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-2.5 bg-white text-black font-semibold rounded-sm hover:bg-gray-200 disabled:opacity-60 transition-colors"
      >
        {saving ? "Saving…" : "Next"}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
